import React, { useState, useEffect, useCallback } from 'react';
import FlyerViewer from '../../components/FlyerViewer';
import Seo from '../../components/Seo';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  MapPin, Monitor, Users, User, BadgeCheck, Star, Mail, Search as SearchIcon,
  Scale, MessageSquare, Handshake, ShieldCheck, Target, Heart,
} from 'lucide-react';
import { US_STATES, SPECIALTIES, LICENSE_TYPES } from './constants';
import { useJsonLd, SITE_URL } from './useJsonLd';

const SupervisorDirectory = () => {
  const [supervisors, setSupervisors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    state: '', city: '', delivery: '', supervision_type: '', license_type: '',
    specialty: '', price_max: '', accepting_new: false, search: '',
  });
  const [draft, setDraft] = useState(filters);

  useJsonLd('supervisor-directory-schema', {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Find a Clinical Supervisor',
    description: 'Directory of verified LCSW-S clinical supervisors offering online and in-person social work supervision toward LCSW licensure.',
    url: `${SITE_URL}/find-a-supervisor`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: supervisors.filter((s) => !s.is_demo).map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Person',
          name: s.full_name,
          jobTitle: `${s.license_type} Clinical Supervisor`,
          url: `${SITE_URL}/supervisors/${s.user_id}`,
          address: { '@type': 'PostalAddress', addressRegion: s.license_state, addressCountry: 'US' },
        },
      })),
    },
  });

  const fetchSupervisors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== false) params[k] = v; });
      const res = await axios.get(`${API_URL}/supervisors`, { params });
      setSupervisors(res.data.supervisors || []);
      setTotal(res.data.total || 0);
    } catch (e) { setSupervisors([]); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchSupervisors(); }, [fetchSupervisors]);

  const setD = (k, v) => setDraft((f) => ({ ...f, [k]: v }));
  const applySearch = () => setFilters(draft);
  const clearAll = () => {
    const empty = { state: '', city: '', delivery: '', supervision_type: '', license_type: '', specialty: '', price_max: '', accepting_new: false, search: '' };
    setDraft(empty); setFilters(empty);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        exactTitle="Find an LCSW-S Supervisor | Clinical Social Work Supervision | INASW"
        description="Connect with verified LCSW-S clinical supervisors nationwide for online and in-person social work supervision toward your LCSW. Search by state, delivery, license type and specialty."
        path="/find-a-supervisor"
      />
      {/* Photo hero banner (integrated, with overlay) */}
      <section className="relative pt-16">
        <div className="relative h-72 sm:h-80 lg:h-96 w-full overflow-hidden">
          <img src="/supervisor-hero-photo.png" alt="Clinical supervisor meeting with a social worker via telehealth" className="w-full h-full object-cover object-center" data-testid="supervisor-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/60 to-primary/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-xl text-white">
                <h1 className="font-serif text-4xl sm:text-5xl font-bold" data-testid="supervisor-directory-title">Find a Clinical Supervisor</h1>
                <p className="text-white/90 mt-3 text-base sm:text-lg">Connect with verified LCSW-S clinical supervisors for online and in-person supervision toward your LCSW.</p>
                <Link to="/become-a-supervisor">
                  <Button className="mt-5 bg-secondary hover:bg-secondary-hover text-white" data-testid="become-supervisor-cta">Become a Listed Supervisor</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INASW Clinical Supervisors flyer */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <FlyerViewer src="/flyer-clinical-supervisor.png" alt="INASW Clinical Supervisors — Your Expertise. Their Future." />
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar filters */}
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border p-5 sticky top-24">
            <h2 className="font-serif text-lg font-bold text-primary mb-4">Search Supervisors</h2>
            <div className="space-y-4">
              <FilterSelect label="State" value={draft.state} onChange={(v) => setD('state', v)} options={US_STATES} testid="filter-state" placeholder="Select State" />
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">City or ZIP Code</label>
                <Input placeholder="Enter city or ZIP code" value={draft.city} onChange={(e) => setD('city', e.target.value)} data-testid="filter-city" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Supervision Format</p>
                {[['online', 'Online'], ['in_person', 'In Person'], ['both', 'Both']].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-1">
                    <Checkbox checked={draft.delivery === v} onCheckedChange={(c) => setD('delivery', c ? v : '')} data-testid={`filter-delivery-${v}`} /> {l}
                  </label>
                ))}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Supervision Type</p>
                {[['individual', 'Individual'], ['group', 'Group']].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-1">
                    <Checkbox checked={draft.supervision_type === v} onCheckedChange={(c) => setD('supervision_type', c ? v : '')} data-testid={`filter-type-${v}`} /> {l}
                  </label>
                ))}
              </div>
              <FilterSelect label="License Level" value={draft.license_type} onChange={(v) => setD('license_type', v)} options={LICENSE_TYPES} testid="filter-license" placeholder="Any license" />
              <FilterSelect label="Specialty" value={draft.specialty} onChange={(v) => setD('specialty', v)} options={SPECIALTIES} testid="filter-specialty" placeholder="Select Specialty" />
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Max Price / session ($)</label>
                <Input type="number" placeholder="Any" value={draft.price_max} onChange={(e) => setD('price_max', e.target.value)} data-testid="filter-price" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <Checkbox checked={draft.accepting_new} onCheckedChange={(v) => setD('accepting_new', !!v)} data-testid="filter-accepting-new" /> Accepting new supervisees
              </label>
              <Button className="w-full bg-primary hover:bg-primary/90 text-white" onClick={applySearch} data-testid="search-supervisors-btn">
                <SearchIcon className="w-4 h-4 mr-2" /> Search Supervisors
              </Button>
              <button className="w-full text-center text-secondary text-sm font-medium hover:underline" onClick={clearAll} data-testid="clear-filters-btn">Clear Filters</button>
            </div>
          </div>
        </aside>

        {/* Matches */}
        <main className="lg:col-span-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-bold text-primary">Supervisor Matches</h2>
            <span className="text-sm text-gray-500" data-testid="supervisor-count">{loading ? 'Loading…' : `${total} result${total === 1 ? '' : 's'} found`}</span>
          </div>
          {!loading && supervisors.length === 0 && (
            <div className="bg-white rounded-2xl border-2 border-secondary/30 p-8 sm:p-10 text-center" data-testid="no-supervisors">
              <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <SearchIcon className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-serif text-2xl text-primary font-bold mb-2">We couldn't find a supervisor matching that yet.</h3>
              <p className="text-gray-600 max-w-xl mx-auto">
                New LCSW-S supervisors join INASW regularly. Tell us what you're looking for and we'll notify you
                the moment a matching supervisor is available — whether you're ready to start hours now or just exploring.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <Link to="/register?type=job_seeker">
                  <Button className="bg-secondary hover:bg-secondary-hover text-white" data-testid="notify-supervisor-btn">Notify Me When One Is Available</Button>
                </Link>
                <Button variant="outline" onClick={clearAll} data-testid="widen-search-btn">Widen My Search</Button>
                <Link to="/become-a-supervisor">
                  <Button variant="outline" data-testid="are-you-supervisor-btn">Are you an LCSW-S? List your practice</Button>
                </Link>
              </div>
            </div>
          )}
          <div className="space-y-4">
            {supervisors.map((s) => <SupervisorCard key={s.user_id} s={s} />)}
          </div>

          <p className="text-xs text-gray-400 mt-6 bg-white rounded-xl border p-4">
            Supervisor credentials are verified through the official Texas BHEC public license-verification system. Listing on this website does not replace verification with the applicable state licensing board, and does not constitute an endorsement.
          </p>
        </main>

        {/* What to look for */}
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-2xl border p-6 sticky top-24">
            <h3 className="font-serif text-xl font-bold text-primary mb-5">What to Look For</h3>
            <div className="space-y-5">
              <Tip icon={ShieldCheck} title="Credentials" text="Ensure the supervisor holds the appropriate license and supervisor designation." />
              <Tip icon={User} title="Fit" text="Choose someone whose approach aligns with your learning style and goals." />
              <Tip icon={Monitor} title="Format" text="Decide whether online, in-person, or both formats work best for you." />
              <Tip icon={Target} title="Supervision Goals" text="Find a supervisor who supports your specific professional development goals." />
            </div>
          </div>
        </aside>
      </div>

      {/* How it works */}
      <section className="bg-white border-y py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-bold text-primary text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            <Step icon={SearchIcon} num="1" title="Search" text="Use filters to find supervisors that meet your needs." />
            <Step icon={Scale} num="2" title="Compare" text="Review profiles, specialties, and formats to find the right match." />
            <Step icon={MessageSquare} num="3" title="Connect" text="Reach out to supervisors to ask questions and discuss fit." />
            <Step icon={Handshake} num="4" title="Begin Supervision" text="Confirm and start your supervision journey with confidence." />
          </div>
        </div>
      </section>

      {/* Trust footer band */}
      <section className="bg-primary py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-6 justify-between text-white">
          <div className="flex items-center gap-3">
            <Heart className="w-9 h-9 text-secondary" />
            <div>
              <p className="font-serif text-xl font-bold">Your journey matters.</p>
              <p className="text-white/80 text-sm italic">Find the right supervisor for your goals.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TrustItem icon={ShieldCheck} title="Verified Professionals" text="License info verified through Texas BHEC." />
            <TrustItem icon={Users} title="Support Your Growth" text="Guidance you need to reach your LCSW." />
          </div>
        </div>
      </section>
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testid, placeholder }) => (
  <div>
    <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder || `Any ${label.toLowerCase()}`}</SelectItem>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lab = typeof o === 'string' ? o : o.label;
          return <SelectItem key={val} value={val}>{lab}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  </div>
);

const deliveryLabel = (d) => (d === 'both' ? 'Online & In Person' : d === 'online' ? 'Online' : 'In Person');
const supTypeLabel = (t) => (t === 'both' ? 'Individual & Group' : t === 'individual' ? 'Individual' : 'Group');

export const supervisionRate = (s) => {
  if (!s.display_rate) return 'Contact for supervision rates';
  const parts = [];
  if (s.price_per_session) parts.push(`$${s.price_per_session} per supervision session`);
  if (s.group_price_per_session) parts.push(`$${s.group_price_per_session} per group supervision session`);
  return parts.length ? parts.join(' · ') : 'Contact for supervision rates';
};

export const SupervisorCard = ({ s }) => {
  const feat = !!s.is_featured && !s.is_demo;
  const photoSize = feat ? 'w-28 h-28' : 'w-24 h-24';
  return (
    <div
      className={`bg-white rounded-2xl border p-5 flex flex-col sm:flex-row gap-4 transition-shadow hover:shadow-md ${feat ? 'border-[#CBA14B] ring-1 ring-[#CBA14B]/40 shadow-[0_0_0_1px_rgba(203,161,75,0.25)]' : ''}`}
      data-testid={`supervisor-card-${s.user_id}`}
    >
      <div className="flex-shrink-0">
        {s.photo_url ? (
          <img src={s.photo_url} alt={s.full_name} className={`${photoSize} rounded-xl object-cover ${feat ? 'ring-2 ring-[#CBA14B]' : ''}`} />
        ) : (
          <div className={`${photoSize} rounded-xl bg-primary/10 flex items-center justify-center`}><User className="w-10 h-10 text-primary" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h3 className="font-serif text-xl font-bold text-primary leading-tight">{s.full_name}</h3>
            {s.license_type && <p className="text-xs font-semibold text-gray-500">{s.license_type}</p>}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {feat && (
              <Badge className="bg-[#CBA14B] text-white border-0 flex items-center gap-1" data-testid={`featured-badge-${s.user_id}`}><Star className="w-3.5 h-3.5 fill-white" /> FEATURED</Badge>
            )}
            {!s.is_demo && (
              <Badge className="bg-green-100 text-green-800 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> License Verified · Texas BHEC</Badge>
            )}
          </div>
        </div>
        {/* Availability */}
        {s.accepting_new && !s.is_demo ? (
          <p className="text-xs font-semibold text-green-700 mt-1.5">● Accepting New Supervisees</p>
        ) : (
          <p className="text-xs font-medium text-gray-400 mt-1.5">Not Currently Accepting New Supervisees</p>
        )}
        <p className="text-sm text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.city ? `${s.city}, TX` : s.license_state}</span>
          <span className="inline-flex items-center gap-1"><Monitor className="w-3.5 h-3.5" /> {deliveryLabel(s.delivery)}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {supTypeLabel(s.supervision_type)}</span>
        </p>
        {s.bio && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{s.bio}</p>}
        {(s.specialties || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {s.specialties.slice(0, 4).map((sp) => <Badge key={sp} variant="outline" className="text-xs">{sp}</Badge>)}
          </div>
        )}
        <p className="text-sm font-semibold text-primary mt-2.5" data-testid={`sup-rate-${s.user_id}`}>{supervisionRate(s)}</p>
        <div className="mt-3 flex gap-2">
          <Link to={`/supervisors/${s.user_id}`}>
            <Button size="sm" variant="outline" data-testid={`view-supervisor-${s.user_id}`}>View Profile</Button>
          </Link>
          {!s.is_demo && s.accepting_new && s.allow_contact !== false && (
            <Link to={`/supervisors/${s.user_id}`}>
              <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" data-testid={`contact-supervisor-${s.user_id}`}><Mail className="w-4 h-4 mr-1" /> Contact Supervisor</Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const Tip = ({ icon: Icon, title, text }) => (
  <div className="flex gap-3">
    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
    <div><p className="font-semibold text-primary text-sm">{title}</p><p className="text-sm text-gray-500">{text}</p></div>
  </div>
);

const Step = ({ icon: Icon, num, title, text }) => (
  <div className="text-center">
    <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-3"><Icon className="w-5 h-5" /></div>
    <p className="font-semibold text-primary">{num}. {title}</p>
    <p className="text-sm text-gray-500 mt-1">{text}</p>
  </div>
);

const TrustItem = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-6 h-6 text-secondary flex-shrink-0" />
    <div><p className="font-semibold">{title}</p><p className="text-white/70 text-sm">{text}</p></div>
  </div>
);

export default SupervisorDirectory;
